import React, { useState } from 'react';
import styles from './styles.module.scss';
import Profile from './Profile';
import Prizes from './Prizes';
import Media from './Media';
import Guide from './Guide';
import { useModal } from '../../context/ModalContext';
import { gql } from '../../utils/graphql';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import instaIcon from '../../assets/icons/instagram.svg';
import tiktokIcon from '../../assets/icons/tiktok.svg';
import whatsappIcon from '../../assets/icons/whatsapp.svg';

interface DashboardProps {
	ambassador: any;
	token: string;
	onLogout: () => void;
	refreshData: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ ambassador, token, onLogout, refreshData }) => {
	const { confirm } = useModal();
	const [activeTab, setActiveTab] = useState<'prizes' | 'settings' | 'media' | 'guide'>('prizes');
	const [uploading, setUploading] = useState(false);
	const [imgMsg, setImgMsg] = useState('');

	const handleLogout = async () => {
		const ok = await confirm({
			title: 'Deconectare',
			content: 'Ești sigur că vrei să te deconectezi?'
		});
		if (ok) onLogout();
	};

	const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		setUploading(true);
		setImgMsg('');

		try {
			const formData = new FormData();
			formData.append('file', file);
			formData.append('folder', 'ambassadors');

			const uploadRes = await axios.post(`${API_BASE_URL}/api/upload`, formData, {
				withCredentials: true,
				headers: {
					'Content-Type': 'multipart/form-data',
				}
			});

			if (!uploadRes.data.success) throw new Error('Încărcarea a eșuat');

			const imageUrl = uploadRes.data.url;

			const query = `
				mutation UpdateProfile($id: ID!, $input: UpdateAmbassadorInput!) {
					updateAmbassador(id: $id, input: $input) { id profileImage }
				}
			`;

			const response = await gql(query, {
				id: ambassador.id,
				input: { profileImage: imageUrl }
			}, token);

			if (response.data?.errors) throw new Error(response.data.errors[0].message);

			setImgMsg('Fotografie actualizată!');
			refreshData();
			setTimeout(() => setImgMsg(''), 3000);
		} catch (err: any) {
			console.error(err);
			setImgMsg(`Error: ${err.message}`);
		} finally {
			setUploading(false);
		}
	};

	if (!ambassador) return <div style={{ textAlign: 'center', padding: '4rem', color: 'rgba(255,255,255,0.5)' }}>Se încarcă...</div>;

	const tabs = [
		{ key: 'prizes' as const, label: 'Premii' },
		{ key: 'media' as const, label: 'Media' },
		{ key: 'guide' as const, label: 'Ghid & Info' },
		{ key: 'settings' as const, label: 'Setări' },
	];

	return (
		<div>
			{/* ---- Profile Header ---- */}
			<div className={styles.profileHeader}>
				<div className={styles.avatar}>
					{ambassador.profileImage ? (
						<img src={ambassador.profileImage} alt={`${ambassador.firstName} ${ambassador.lastName}`} />
					) : (
						<div className={styles.avatarFallback}>
							{ambassador.firstName?.[0]}{ambassador.lastName?.[0]}
						</div>
					)}
					<label className={styles.avatarOverlay}>
						{uploading ? '...' : 'Editează'}
						<input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} disabled={uploading} />
					</label>
				</div>

				<div className={styles.profileInfo}>
					<h1 className={styles.displayName}>{ambassador.firstName} {ambassador.lastName}</h1>
					<div className={styles.userHandle}>{ambassador.email}</div>

					{imgMsg && (
						<div className={`${styles.msgBox} ${imgMsg.includes('Error') ? styles.msgError : styles.msgSuccess}`} style={{ marginBottom: '0.5rem' }}>
							{imgMsg}
						</div>
					)}

					<div className={styles.socialHeader}>
						{ambassador.instagram && (
							<a
								href={`https://instagram.com/${ambassador.instagram.replace('@', '')}`}
								target="_blank"
								rel="noopener noreferrer"
								className={styles.socialLink}
							>
								<img src={instaIcon} alt="Instagram" />
								<span>@{ambassador.instagram.replace('@', '')}</span>
							</a>
						)}
						{ambassador.tiktok && (
							<a
								href={`https://tiktok.com/@${ambassador.tiktok.replace('@', '')}`}
								target="_blank"
								rel="noopener noreferrer"
								className={styles.socialLink}
							>
								<img src={tiktokIcon} alt="TikTok" />
								<span>@{ambassador.tiktok.replace('@', '')}</span>
							</a>
						)}
						<a
							href="https://chat.whatsapp.com/Ht4M3ATTveGGsipgUJCaIm"
							target="_blank"
							rel="noopener noreferrer"
							className={styles.socialLink}
						>
							<img src={whatsappIcon} alt="WhatsApp" />
							<span>Grup WhatsApp</span>
						</a>
					</div>

					<div className={styles.inlineStats}>
						<div className={styles.inlineStat}>
							<span className={styles.inlineStatNum}>{(ambassador.points || 0) - (ambassador.spentPoints || 0)}</span>
							<span className={styles.inlineStatLabel}>
								{((ambassador.points || 0) - (ambassador.spentPoints || 0)) === 1 ? 'punct rămas' : 'puncte rămase'}
							</span>
						</div>
						<div className={styles.inlineStat}>
							<span className={styles.inlineStatNum}>{ambassador.soldTickets || 0}</span>
							<span className={styles.inlineStatLabel}>
								{ambassador.soldTickets === 1 ? 'bilet vândut' : 'bilete vândute'}
							</span>
						</div>
					</div>
				</div>

				<button className={styles.logoutBtn} onClick={handleLogout}>Deconectare</button>
			</div>

			{/* ---- Codes Banner ---- */}
			{(() => {
				const displayCodes = (ambassador.discountCodes || [])
					.map((dc: any) => (typeof dc === 'string' ? dc : dc.code))
					.filter(Boolean);
				if (displayCodes.length === 0) return null;
				return (
					<div className={styles.codesBanner}>
						<span className={styles.codesLabel}>
							{displayCodes.length === 1 ? 'Codul Meu' : 'Codurile Mele'}
						</span>
						<div className={styles.codesWrap}>
							{displayCodes.map((code: string, idx: number) => (
								<span key={idx} className={styles.codeChip}>{code}</span>
							))}
						</div>
						<div className={styles.conversionHint}>
							4 bilete vândute = 1 punct
						</div>
					</div>
				);
			})()}

			{/* ---- Pill Tabs ---- */}
			<nav className={styles.nav}>
				{tabs.map(tab => (
					<button
						key={tab.key}
						className={`${styles.navItem} ${activeTab === tab.key ? styles.navActive : ''}`}
						onClick={() => setActiveTab(tab.key)}
					>
						{tab.label}
					</button>
				))}
			</nav>

			{/* ---- Tab Content ---- */}
			<div className={styles.panel} key={activeTab}>
				{activeTab === 'prizes' && (
					<Prizes ambassador={ambassador} token={token} refreshData={refreshData} />
				)}

				{activeTab === 'media' && (
					<Media token={token} />
				)}

				{activeTab === 'guide' && (
					<Guide onNavigateTab={(tab) => setActiveTab(tab)} />
				)}

				{activeTab === 'settings' && (
					<Profile ambassador={ambassador} token={token} refreshData={refreshData} />
				)}
			</div>
		</div>
	);
};

export default Dashboard;
